import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@heroui/react';
import { renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportSubmitState } from '../../importSubmit';

type ImportFailureAlertProps = {
    /** What the last import said; nothing renders for `idle`. */
    submit: ImportSubmitState;
    /** Import again. */
    onRetry: () => void;
    /** Retry waits for what the Import button waits for. */
    isRetryDisabled: boolean;
};

/**
 * Why the last import did not go through, in danger tone: "Nothing was
 * imported", then the import's own sentences (one, or a list), or that it
 * did not answer, and Try again, which imports again. Nothing was written
 * and the step kept everything, so the alert says only what to do next.
 */
export const ImportFailureAlert: React.FC<ImportFailureAlertProps> = ({
    submit,
    onRetry,
    isRetryDisabled,
}) => {
    const { t } = useTranslation('pages');

    if (submit.status === 'idle') {
        return null;
    }

    return (
        <Alert
            status="danger"
            {...getNodeTestIdProp('catalog-import-products-import-failed')}
        >
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>
                    {t('catalogImport.products.import.failedTitle')}
                </Alert.Title>
                {submit.status === 'failed' ? (
                    <Alert.Description>
                        {t('catalogImport.products.import.noAnswer')}
                    </Alert.Description>
                ) : submit.notices.length === 1 && submit.notices[0] ? (
                    <Alert.Description>
                        {renderNotice(t, submit.notices[0])}
                    </Alert.Description>
                ) : (
                    <Alert.Description>
                        <ul className="mt-1 list-inside list-disc space-y-1">
                            {submit.notices.map((notice) => (
                                <li key={notice.key}>
                                    {renderNotice(t, notice)}
                                </li>
                            ))}
                        </ul>
                    </Alert.Description>
                )}
                <Button
                    className="mt-2 self-start"
                    isDisabled={isRetryDisabled}
                    onPress={onRetry}
                    size="sm"
                    variant="danger"
                    {...getNodeTestIdProp(
                        'catalog-import-products-import-retry',
                    )}
                >
                    {t('catalogImport.products.import.retry')}
                </Button>
            </Alert.Content>
        </Alert>
    );
};
